import {
  TAuthConverter,
  TConvertor,
  TCreateConverter,
  TFetchConverter,
} from 'data/types/Converter.types';
import {
  EFollowUpMedium,
  EPaymentStatus,
  EPaymentStatusUi,
  EPetStatusDB,
  EPetStatusUI,
  EResidencyStateDB,
  EResidencyStateUI,
  ETenantDetailsStatusDB,
  ETenantDetailsStatusUI,
  ETrainingStatus,
  ETrainingStatusUi,
  TAddTenantFollowUpItemDB,
  TAddTenantFollowUpItemUI,
  TAnimalDetailSADocPacketCreate,
  TAnimalDetailSADocPacketCreateUI,
  TAnimalMetadata,
  TAnimalMetadataUI,
  TApplicationProfile,
  TApplicationProfileUI,
  TChangeTenantEmailDB,
  TChangeTenantEmailUI,
  TEditTenantFollowUpItemDB,
  TEditTenantFollowUpItemUI,
  TFollowUpItemDB,
  TFollowUpItemUI,
  TMarkTenantAsKnownDB,
  TMarkTenantAsKnownUI,
  TPetInfoDB,
  TPetInfoUI,
  TTransferAnimalOptionsDB,
  TTransferAnimalOptionsUI,
  TAnimalTransferPreviewCarriedFeeDB,
  TAnimalTransferPreviewCarriedItemDB,
  TAnimalTransferPreviewDB,
  TAnimalTransferPreviewRecordDB,
  TAnimalTransferPreviewResidencyDB,
  TAnimalTransferPreviewUI,
  TResidencyDataDB,
  TResidencyDataUI,
  TResidentDataDB,
  TResidentDataUI,
  TTenantAnimalDataDB,
  TTenantAnimalDataUI,
  TTenantDB,
  TTenantDetailsDB,
  TTenantDetailsUI,
  TTenantInfoDB,
  TTenantInfoUI,
  TTenantMaskedDataDB,
  TTenantMaskedDataUI,
  TTenantSearchListDB,
  TTenantSearchListUI,
  TTenantUI,
  TTraining,
  TTrainingUi,
  TTransferAnimalBodyDB,
  TTransferAnimalBodyUI,
  TSplitTenantDB,
  TSplitTenantUI,
  TTenantSplitOptionsDB,
  TTenantSplitOptionsUI,
} from 'data/types/Tenant.types';

import { omit, pick } from 'lodash';
import {
  animalChargingStatusConverter,
  failsConvertor,
  filtersConverter,
  formattedDate,
  formattedDateTimezone,
  getFirstLetters,
  propertyInfoConverter,
  propertyManagementInfoConverter,
  readableBooleanFieldConvertor,
  readableFieldConvertor,
  statusChangeInfoConverter,
  statusInfoWithResourceConverter,
  verificationStatusConverter,
} from './General.converters';
import { getHPDocPacketsConvertor } from './DocPacket.convertors';
import {
  animalPaymentStatusConverter,
  animalStatusConverter,
  animalStatusDetailsConverter,
  animalTypesConvertor,
  notBeingChargedReasonConverter,
} from './Animal.converters';
import { tagConverter, tenantTagConverter } from './Tags.converters';
import {
  EAnimalPetChargeStatusDB,
  EAnimalPetChargeStatusUI,
} from '../types/GeneralTypes';
import { EAnimalTypes, EAnimalTypesUI } from '../types/Animal.types';
import { trainerInfoConvertor } from './AnimalTraining.convertors';
import { dateTimeFormat } from 'helpers/utils';

export const getTenantSearchListConvertor: TFetchConverter<
  TTenantSearchListUI[],
  TTenantSearchListDB[]
> = {
  fromDb: data =>
    data.map(item => ({
      label: item.name,
      value: item.id,
    })),
};
export const animalMetadataConvertor: TFetchConverter<
  TAnimalMetadataUI,
  TAnimalMetadata
> = {
  fromDb: data => {
    if (!data) {
      return null;
    }

    return {
      present: data.present,
      description: data?.description || null,
    };
  },
};

export const trainingStatusConvertor: TConvertor<
  ETrainingStatusUi,
  ETrainingStatus
> = {
  fromDb: data => {
    return ETrainingStatusUi[
      ETrainingStatus[data] as keyof typeof ETrainingStatusUi
    ];
  },
  toDb: data => {
    return ETrainingStatus[
      ETrainingStatusUi[data] as keyof typeof ETrainingStatus
    ];
  },
};

export const paymentStatusConvertor: TConvertor<
  EPaymentStatusUi,
  EPaymentStatus
> = {
  fromDb: data => {
    return EPaymentStatusUi[
      EPaymentStatus[data] as keyof typeof EPaymentStatusUi
    ];
  },
  toDb: data => {
    return EPaymentStatus[
      EPaymentStatusUi[data] as keyof typeof EPaymentStatus
    ];
  },
};

export const trainingConvertor: TFetchConverter<TTrainingUi, TTraining> = {
  fromDb: data => {
    return {
      title: data.title,
      status: trainingStatusConvertor.fromDb(data.status),
    };
  },
};
export const applicationProfileConvertor: TFetchConverter<
  TApplicationProfileUI,
  TApplicationProfile
> = {
  fromDb: data => {
    const documentsFails = pick(data.fails || {}, ['hpDocumentPackets']);
    return {
      id: data.userId,
      profilePicture: readableFieldConvertor.fromDb(data.profilePicture),
      name: readableFieldConvertor.fromDb(data.name),
      email: readableFieldConvertor.fromDb(data.email),
      phone: readableFieldConvertor.fromDb(data.phone),
      haveAnimal: readableBooleanFieldConvertor.fromDb(data.haveAnimal),
      fails: failsConvertor.fromDb(omit(data.fails || {}, 'hpDocumentPackets')),
      documentsFails: failsConvertor.fromDb(documentsFails),
      animalMetadata: animalMetadataConvertor.fromDb(
        data.specifiedAnimalMetadata,
      ),
      trainings: data.trainings.map(item => trainingConvertor.fromDb(item)),
      paymentStatus: paymentStatusConvertor.fromDb(data.paymentStatus),
      hpDocumentPackets: data.hpDocumentPackets?.map(packet => ({
        ...getHPDocPacketsConvertor.fromDb(packet),
        fails: failsConvertor.fromDb(packet.fails || {}),
      })),
    };
  },
};

export const tenantResidencyStateConverter: TConvertor<
  EResidencyStateUI,
  EResidencyStateDB
> = {
  fromDb: data => {
    const statuses: {
      [key in EResidencyStateDB]: EResidencyStateUI;
    } = {
      [EResidencyStateDB.NONE]: EResidencyStateUI.NONE,
      [EResidencyStateDB.APPLICANT]: EResidencyStateUI.APPLICANT,
      [EResidencyStateDB.RESIDENT]: EResidencyStateUI.RESIDENT,
      [EResidencyStateDB.FUTURE]: EResidencyStateUI.FUTURE,
      [EResidencyStateDB.NOTICE]: EResidencyStateUI.NOTICE,
    };
    return statuses[data];
  },
  toDb: data => {
    const statuses: {
      [key in EResidencyStateUI]: EResidencyStateDB;
    } = {
      [EResidencyStateUI.NONE]: EResidencyStateDB.NONE,
      [EResidencyStateUI.APPLICANT]: EResidencyStateDB.APPLICANT,
      [EResidencyStateUI.RESIDENT]: EResidencyStateDB.RESIDENT,
      [EResidencyStateUI.FUTURE]: EResidencyStateDB.FUTURE,
      [EResidencyStateUI.NOTICE]: EResidencyStateDB.NOTICE,
    };
    return statuses[data];
  },
};

export const tenantDetailsStatusConverter: TConvertor<
  ETenantDetailsStatusUI,
  ETenantDetailsStatusDB
> = {
  fromDb: data => {
    const statuses: {
      [key in ETenantDetailsStatusDB]: ETenantDetailsStatusUI;
    } = {
      [ETenantDetailsStatusDB.APPROVED]: ETenantDetailsStatusUI.APPROVED,
      [ETenantDetailsStatusDB.PENDING]: ETenantDetailsStatusUI.PENDING,
      [ETenantDetailsStatusDB.MOVED_IN]: ETenantDetailsStatusUI.MOVED_IN,
      [ETenantDetailsStatusDB.REJECTED]: ETenantDetailsStatusUI.REJECTED,
      [ETenantDetailsStatusDB.RETRACTED]: ETenantDetailsStatusUI.RETRACTED,
      [ETenantDetailsStatusDB.MOVED_OUT]: ETenantDetailsStatusUI.MOVED_OUT,
      [ETenantDetailsStatusDB.NOTICE]: ETenantDetailsStatusUI.NOTICE,
    };
    return statuses[data];
  },
  toDb: data => {
    const statuses: {
      [key in ETenantDetailsStatusUI]: ETenantDetailsStatusDB;
    } = {
      [ETenantDetailsStatusUI.APPROVED]: ETenantDetailsStatusDB.APPROVED,
      [ETenantDetailsStatusUI.PENDING]: ETenantDetailsStatusDB.PENDING,
      [ETenantDetailsStatusUI.MOVED_IN]: ETenantDetailsStatusDB.MOVED_IN,
      [ETenantDetailsStatusUI.REJECTED]: ETenantDetailsStatusDB.REJECTED,
      [ETenantDetailsStatusUI.RETRACTED]: ETenantDetailsStatusDB.RETRACTED,
      [ETenantDetailsStatusUI.MOVED_OUT]: ETenantDetailsStatusDB.MOVED_OUT,
      [ETenantDetailsStatusUI.NOTICE]: ETenantDetailsStatusDB.NOTICE,
    };
    return statuses[data];
  },
};
export const petStatusConverter: TConvertor<EPetStatusUI, EPetStatusDB> = {
  fromDb: data => {
    const statuses: {
      [key in EPetStatusDB]: EPetStatusUI;
    } = {
      [EPetStatusDB.HAS_ANIMAL]: EPetStatusUI.HAS_ANIMAL,
      [EPetStatusDB.HAS_NOT_ANIMAL]: EPetStatusUI.HAS_NOT_ANIMAL,
      [EPetStatusDB.NOT_SPECIFIED]: EPetStatusUI.NOT_SPECIFIED,
    };
    return statuses[data];
  },
  toDb: data => {
    const statuses: {
      [key in EPetStatusUI]: EPetStatusDB;
    } = {
      [EPetStatusUI.HAS_ANIMAL]: EPetStatusDB.HAS_ANIMAL,
      [EPetStatusUI.HAS_NOT_ANIMAL]: EPetStatusDB.HAS_NOT_ANIMAL,
      [EPetStatusUI.NOT_SPECIFIED]: EPetStatusDB.NOT_SPECIFIED,
    };
    return statuses[data];
  },
};

export const tenantInfoConverter: TFetchConverter<
  TTenantInfoUI,
  TTenantInfoDB
> = {
  fromDb: ({ tenantId, name, profilePicture }) => {
    return {
      tenantId,
      tenantName: name,
      profilePicture: profilePicture || '',
      firstLetters: getFirstLetters(name),
    };
  },
};

export const petInfoConverter: TFetchConverter<TPetInfoUI, TPetInfoDB> = {
  fromDb: ({ petStatus, petsCount }) => {
    return {
      petStatus: petStatusConverter.fromDb(petStatus),
      petsCount,
    };
  },
};

export const tenantConverter: TAuthConverter<
  { [key: string]: string | string[] } | undefined,
  { [key: string]: string | string[] },
  TTenantUI,
  TTenantDB
> = {
  fromDb: ({
    petStatus,
    petsCount,
    propertyManagementName,
    propertyManagementId,
    propertyManagementLogo,
    tenantId,
    notificationsEnabled,
    name,
    profilePicture,
    email,
    phone,
    username,
    isUsernameEmail,
    propertyName,
    propertyId,
    state,
    statusChangeInfo,
    unitNumber,
    deactivated,
    deactivationStatusChangeInfo,
  }) => {
    return {
      tenantInfo: tenantInfoConverter.fromDb({
        tenantId,
        name,
        profilePicture,
      }),
      email,
      phone,
      username,
      isUsernameEmail,
      notificationsEnabled,
      propertyInfo: propertyInfoConverter.fromDb({ propertyId, propertyName }),
      propertyManagementInfo: propertyManagementInfoConverter.fromDb({
        propertyManagementId,
        propertyManagementName,
        propertyManagementLogo,
      }),
      petInfo: petInfoConverter.fromDb({ petStatus, petsCount }),
      residencyState: tenantResidencyStateConverter.fromDb(state),
      statusChangeInfo: statusChangeInfo
        ? statusInfoWithResourceConverter.fromDb(statusChangeInfo)
        : null,
      unitNumber,
      deactivated: !!deactivated,
      deactivationStatusChangeInfo: deactivationStatusChangeInfo
        ? statusChangeInfoConverter.fromDb(deactivationStatusChangeInfo)
        : null,
    };
  },
  toDb: filter => filtersConverter.toDb(filter),
};

export const animalsConverter: TFetchConverter<
  TTenantAnimalDataUI,
  TTenantAnimalDataDB
> = {
  fromDb: data => {
    return {
      chargingStatusInfo: data.chargingStatusInfo
        ? statusInfoWithResourceConverter.fromDb(data.chargingStatusInfo)
        : null,
      canTagKnown: data.canTagKnown,
      cannotTagKnownReason: data.cannotTagKnownReason,
      profilePicture: data.profilePicture || undefined,
      animalId: data.animalId,
      isToPetConverted: data.isToPetConverted,
      breed: data?.breed ? data.breed?.value : '',
      chargingStatusChangeReason: data.chargingStatusChangeReason || '',
      id: data.id,
      name: data.name,
      chargingStatus: animalChargingStatusConverter.fromDb(data.chargingStatus),
      notBeingChargedReason: data.notBeingChargedReason
        ? notBeingChargedReasonConverter.fromDb(data.notBeingChargedReason)
        : null,
      specie: data.specie ? data.specie?.value : '',
      speciePredefinedId: data.specie?.predefinedId,
      status: animalStatusConverter.fromDb(data.status),
      splitStatus: animalStatusDetailsConverter.fromDb(data.statusDetails),
      verificationStatus: verificationStatusConverter.fromDb(
        data.verificationStatus,
      ),
      type: animalTypesConvertor.fromDb(data.type),
      tags: (data.tags || []).map(item => tagConverter.fromDb(item)),
      discoveredTagReason: data.discoveredTagReason,
      policySignedBy: data.policySignedBy,
      policyDateSigned: data.policyDateSigned
        ? formattedDate(data.policyDateSigned)
        : 'N/A',
      policyName: data.policyName,
      policyContent: data.policyContent,
      signedDocType: data.signedDocType,
      policyId: data.policyId,
      statusChangeInfo: data.statusChangeInfo
        ? statusChangeInfoConverter.fromDb(data.statusChangeInfo)
        : null,
      paidUntil: data.paidUntil ? formattedDate(data.paidUntil) : '',
      paymentStatus: data.paymentStatus
        ? animalPaymentStatusConverter.fromDb(data.paymentStatus)
        : null,
      petRentChargingStatusDetails: data.petRentChargingStatusDetails
        ? {
            changeInfo: data.petRentChargingStatusDetails.changeInfo
              ? statusInfoWithResourceConverter.fromDb(
                  data.petRentChargingStatusDetails.changeInfo,
                )
              : null,
            changeReason: data.petRentChargingStatusDetails.changeReason,
            status: animalChargingStatusConverter.fromDb(
              data.petRentChargingStatusDetails.status,
            ),
          }
        : null,
      petFeeChargingStatusDetails: data.petFeeChargingStatusDetails
        ? {
            changeInfo: data.petFeeChargingStatusDetails.changeInfo
              ? statusInfoWithResourceConverter.fromDb(
                  data.petFeeChargingStatusDetails.changeInfo,
                )
              : null,
            changeReason: data.petFeeChargingStatusDetails.changeReason,
            status: animalChargingStatusConverter.fromDb(
              data.petFeeChargingStatusDetails.status,
            ),
          }
        : null,
    };
  },
};

export const residencyMembersConverter: TFetchConverter<
  TResidentDataUI,
  TResidentDataDB
> = {
  fromDb: data => {
    return {
      tenantId: data.tenantId,
      hasAnimal: data.hasAnimal,
      roommateHasAnimal: data.roommateHasAnimal,
      email: data.email || '',
      name: data.name,
      phone: data.phone || '',
      profilePicture: data.profilePicture || '',
      status: tenantDetailsStatusConverter.fromDb(data.status),
      animals: (data.animals || []).map(item => animalsConverter.fromDb(item)),
      id: data.id,
      leaseToDate: formattedDate(data.leaseToDate),
      moveInDate: formattedDate(data.moveInDate),
      nextLeaseToDate: data.nextLeaseToDate
        ? formattedDate(data.nextLeaseToDate)
        : null,
      tags: data.tags.map(item => tenantTagConverter.fromDb(item)),
      canTagKnown: data.canTagKnown,
      cannotTagKnownReason: data.cannotTagKnownReason,
      statusChangeInfo: data.statusChangeInfo
        ? statusInfoWithResourceConverter.fromDb(data.statusChangeInfo)
        : null,
      contactId: data.contactId,
    };
  },
};

export const tenantResidencyConverter: TFetchConverter<
  TResidencyDataUI,
  TResidencyDataDB
> = {
  fromDb: data => {
    let sa = 0;
    let esa = 0;
    let pet = 0;
    let shouldNotBeCharged = 0;
    let gracePeriod = 0;
    let beingCharged = 0;
    let notBeingCharged = 0;
    for (let i = 0; i < data.members.length; i += 1) {
      for (let j = 0; j < data.members[i]?.animals?.length; j += 1) {
        const animal = data.members[i]?.animals[j];
        if (animal?.type === EAnimalTypes.ESA) {
          esa += 1;
        } else if (animal?.type === EAnimalTypes.PET) {
          pet += 1;
        } else {
          sa += 1;
        }
        switch (animal?.chargingStatus) {
          case EAnimalPetChargeStatusDB.SHOULD_NOT_BE_CHARGE:
            shouldNotBeCharged += 1;
            break;
          case EAnimalPetChargeStatusDB.NOT_BEING_CHARGED:
            notBeingCharged += 1;
            break;
          case EAnimalPetChargeStatusDB.GRACE_PERIOD:
            gracePeriod += 1;
            break;
          case EAnimalPetChargeStatusDB.BEING_CHARGED:
            beingCharged += 1;
            break;
          default:
            break;
        }
      }
    }
    return {
      propertyName: data.propertyName || '',
      propertyId: data.propertyId,
      propertyManagementLogo: data.propertyManagementLogo || '',
      propertyManagementName: data.propertyManagementName || '',
      unitNumber: data.unitNumber || '',
      roommates: data.members?.length - 1,
      hasAnimal: data.hasAnimal,
      roommateHasAnimal: data.roommateHasAnimal,
      policyState: data.policyState,
      propertyManagementId: data.propertyManagementId,
      landlordId: data.landlordId,
      [EAnimalTypesUI.ESA]: esa,
      [EAnimalTypesUI.SA]: sa,
      [EAnimalTypesUI.PET]: pet,
      [EAnimalPetChargeStatusUI.SHOULD_NOT_BE_CHARGE]: shouldNotBeCharged,
      [EAnimalPetChargeStatusUI.NOT_BEING_CHARGED]: notBeingCharged,
      [EAnimalPetChargeStatusUI.GRACE_PERIOD]: gracePeriod,
      [EAnimalPetChargeStatusUI.BEING_CHARGED]: beingCharged,
      members: (data.members || []).map(item =>
        residencyMembersConverter.fromDb(item),
      ),
      shouldTenantSignAddendum: data.shouldTenantSignAddendum,
      paymentAgreementDate: data.paymentAgreementDate
        ? formattedDate(data.paymentAgreementDate)
        : '',
      pricesForOtherAnimals: data.pricesForOtherAnimals,
      priceForFirstAnimal: data.priceForFirstAnimal,
      shouldTenantPayForPet: data.shouldTenantPayForPet,
      contactId: data.contactId,
    };
  },
};

export const tenantDetailsConverter: TFetchConverter<
  TTenantDetailsUI,
  TTenantDetailsDB
> = {
  fromDb: data => {
    return {
      id: data.id,
      name: data.name,
      email: data.email || '',
      phone: data.phone || '',
      notificationsEnabled: data.notificationsEnabled,
      residencies: (data.residencies || []).map(item =>
        tenantResidencyConverter.fromDb(item),
      ),
      deactivationStatusChangeInfo: data.deactivationStatusChangeInfo
        ? statusChangeInfoConverter.fromDb(data.deactivationStatusChangeInfo)
        : null,
      isUsernameEmail: data?.isUsernameEmail,
      username: data?.username ?? null,
    };
  },
};

export const changeTenantEmailConverter: TCreateConverter<
  TChangeTenantEmailUI,
  TChangeTenantEmailDB
> = {
  toDb: data => {
    return {
      newEmail: data.value,
    };
  },
};

export const markTenantAsKnownConverter: TCreateConverter<
  TMarkTenantAsKnownUI,
  TMarkTenantAsKnownDB
> = {
  toDb: data => {
    return {
      tenantResidencyId: data.tenantResidencyId,
    };
  },
};

export const createAnimalDetailSADocPacketsConvertor: TCreateConverter<
  TAnimalDetailSADocPacketCreateUI,
  TAnimalDetailSADocPacketCreate
> = {
  toDb: data => {
    const trainerInfo = {
      name: data.name,
      emailAddress: data.emailAddress,
      faxNumber: data.faxNumber,
      phoneNumber: data.phoneNumber,
    };
    return {
      animalId: data.animalId,
      trainerInfo: trainerInfoConvertor.toDb(trainerInfo),
      saLetters: data.saLetters,
      trainingStartDate: data.startTime
        ? formattedDate(data.startTime, 'YYYY-MM-DD')
        : undefined,
      trainingEndDate: data.endTime
        ? formattedDate(data.endTime, 'YYYY-MM-DD')
        : undefined,
      tenantId: data.tenantId,
    };
  },
};

// export const tenantResidencyStatusConverter: TConvertor<
//   EResidencyStatusUI,
//   EResidencyStatusDB
// > = {
//   fromDb: data => {
//     const dataMapper: { [key in EResidencyStatusDB]: EResidencyStatusUI } = {
//       [EResidencyStatusDB.FUTURE_RESIDENT]: EResidencyStatusUI.FUTURE_RESIDENT,
//       [EResidencyStatusDB.FORMER_RESIDENT]: EResidencyStatusUI.FORMER_RESIDENT,
//       [EResidencyStatusDB.RESIDENT]: EResidencyStatusUI.RESIDENT,
//       [EResidencyStatusDB.APPLICANT]: EResidencyStatusUI.APPLICANT,
//       [EResidencyStatusDB.DENIED]: EResidencyStatusUI.DENIED,
//       [EResidencyStatusDB.RETRACTED]: EResidencyStatusUI.RETRACTED,
//       [EResidencyStatusDB.NOTICE]: EResidencyStatusUI.NOTICE,
//     };
//     return dataMapper[data];
//   },
//   toDb: data => {
//     const dataMapper: { [key in EResidencyStatusUI]: EResidencyStatusDB } = {
//       [EResidencyStatusUI.FUTURE_RESIDENT]: EResidencyStatusDB.FUTURE_RESIDENT,
//       [EResidencyStatusUI.FORMER_RESIDENT]: EResidencyStatusDB.FORMER_RESIDENT,
//       [EResidencyStatusUI.RESIDENT]: EResidencyStatusDB.RESIDENT,
//       [EResidencyStatusUI.APPLICANT]: EResidencyStatusDB.APPLICANT,
//       [EResidencyStatusUI.DENIED]: EResidencyStatusDB.DENIED,
//       [EResidencyStatusUI.RETRACTED]: EResidencyStatusDB.RETRACTED,
//       [EResidencyStatusUI.NOTICE]: EResidencyStatusDB.NOTICE,
//     };
//     return dataMapper[data];
//   },
// };

export const transferAnimalOptionsConverter: TFetchConverter<
  TTransferAnimalOptionsUI[],
  TTransferAnimalOptionsDB
> = {
  fromDb: data => {
    return (data.animalStatuses || []).map(item => ({
      animalId: item.animalId,
      isTransferable: item.transferable,
      splitStatus: animalStatusDetailsConverter.fromDb(item.statusDetails),
      verificationStatus: (item.verificationStatus || []).map(status =>
        verificationStatusConverter.fromDb(status),
      ),
    }));
  },
};

export const transferAnimalBodyConvertor: TCreateConverter<
  TTransferAnimalBodyUI,
  TTransferAnimalBodyDB
> = {
  toDb: data => {
    return {
      targetTenantResidencyId: data.targetTenantResidencyId,
      sourceTenantResidencyId: data.sourceTenantResidencyId,
      animals: data.animals.map(animal => ({
        animalId: animal.animalId,
        verificationStatus:
          animal.verificationStatus &&
          verificationStatusConverter.toDb(animal.verificationStatus),
      })),
      recordEndingsConfirmed: !!data.recordEndingsConfirmed,
      sameResident: !!data.sameResident,
    };
  },
};

const transferPreviewResidencyLabel = (
  residency: TAnimalTransferPreviewResidencyDB,
) =>
  `${residency.propertyName || 'Custom property'} - Unit ${
    residency.unitNumber || 'N/A'
  }`;

const transferPreviewEnding = (record: TAnimalTransferPreviewRecordDB) => {
  const label = transferPreviewResidencyLabel(record.from);
  switch (record.type) {
    case 'ANIMAL_RESIDENCY':
      return `Moved out of ${label}`;
    case 'APPLICATION':
      return `Application on ${label} retracted`;
    case 'DISCOVERED_TAG':
      return `Discovered tag removed on ${label}`;
    case 'KNOWN_TAG':
      return `Known tag removed on ${label}`;
    case 'PNC_SCHEDULE':
      return `Scheduled ${
        record.chargeType === 'PET_RENT' ? 'pet rent' : 'pet fee'
      } "not being charged" tag removed on ${label}`;
    default:
      return `Record ended on ${label}`;
  }
};

const transferPreviewCarriedLabels: Record<
  TAnimalTransferPreviewCarriedItemDB,
  string
> = {
  VERIFICATION: 'verification',
  PHOTOS: 'photos',
  PROFILE_PICTURE: 'profile picture',
  VET_RECORDS: 'vet records',
  MICROCHIP: 'microchip',
  PET_FEE: 'pet profile fee',
};

const transferPreviewCarriedLabel = (
  item: TAnimalTransferPreviewCarriedItemDB,
  fee: TAnimalTransferPreviewCarriedFeeDB | null,
) => {
  const label = transferPreviewCarriedLabels[item] || item;
  if (item !== 'PET_FEE' || !fee) {
    return label;
  }
  const paidThrough = fee.paidThrough
    ? `paid through ${formattedDate(fee.paidThrough)}, `
    : '';
  return `${label} (${paidThrough}onto ${transferPreviewResidencyLabel(
    fee.to,
  )})`;
};

export const animalTransferPreviewConverter: TFetchConverter<
  TAnimalTransferPreviewUI,
  TAnimalTransferPreviewDB
> = {
  fromDb: data => ({
    animals: (data.animals || []).map(animal => ({
      animalId: animal.animalId,
      animalName: animal.animalName || 'Unnamed animal',
      isConsolidation: animal.kind === 'CONSOLIDATE',
      endings: (animal.endings || []).map(transferPreviewEnding),
      newApplications: (animal.newApplications || []).map(
        residency =>
          `New pending application on ${transferPreviewResidencyLabel(
            residency,
          )}`,
      ),
      carried: (animal.carried || []).map(item =>
        transferPreviewCarriedLabel(item, animal.fee),
      ),
      blockers: animal.blockers || [],
    })),
    confirmationRequired: !!data.confirmationRequired,
    blocked: !!data.blocked,
  }),
};

export const tenantMaskedDataConverter: TFetchConverter<
  TTenantMaskedDataUI,
  TTenantMaskedDataDB
> = {
  fromDb: data => {
    return {
      id: data.id,
      name: data.name,
      email: data.email || '',
      phone: data.phone || '',
    };
  },
};

export const getFollowUpItemMediumDisplayValue = (medium: EFollowUpMedium) => {
  const mediumDisplayValues: { [key in EFollowUpMedium]: string } = {
    [EFollowUpMedium.SMS]: 'Text Message',
    [EFollowUpMedium.EMAIL]: 'Email Notification',
  };
  return mediumDisplayValues[medium];
};

export const tenantFollowUpItemConverter: TFetchConverter<
  TFollowUpItemUI,
  TFollowUpItemDB
> = {
  fromDb: data => {
    return {
      id: data.id,
      animals: data.animals || [],
      author: data.author,
      description: data.description,
      isEditable: data.isEditable,
      medium: data.medium,
      occurredOn: formattedDateTimezone(data.occurredOn, dateTimeFormat),
    };
  },
};

export const addTenantFollowUpItemConverter: TCreateConverter<
  TAddTenantFollowUpItemUI,
  TAddTenantFollowUpItemDB
> = {
  toDb: data => {
    return {
      tenantId: data.tenantId,
      description: data.description,
      animalIds: data.animalIds?.length ? data.animalIds : null,
    };
  },
};

export const editTenantFollowUpItemConverter: TCreateConverter<
  TEditTenantFollowUpItemUI,
  TEditTenantFollowUpItemDB
> = {
  toDb: data => {
    const { initialAnimals, finalAnimals } = data;
    const addedAnimals = finalAnimals.filter(
      id => !initialAnimals.includes(id),
    );
    const removedAnimals = initialAnimals.filter(
      id => !finalAnimals.includes(id),
    );
    return {
      tenantId: data.tenantId,
      description: data.description,
      tenantFollowUpItemId: data.tenantFollowUpItemId,
      addedAnimals: addedAnimals.length ? addedAnimals : null,
      removedAnimals: removedAnimals.length ? removedAnimals : null,
    };
  },
};

export const splitTenantConverter: TCreateConverter<
  TSplitTenantUI,
  TSplitTenantDB
> = {
  toDb: data => ({
    sourceTenantId: data.sourceTenantId,
    residencyIds: data.residencyIds,
    destinationTenantId: data.destinationTenantId || null,
    newProfile: data.newProfile
      ? {
          name: data.newProfile.name.trim(),
          email: data.newProfile.email?.trim() || null,
          phone: data.newProfile.phone?.trim() || null,
        }
      : null,
    movedLogin: data.movedLogin || null,
  }),
};

export const tenantSplitOptionsConverter: TFetchConverter<
  TTenantSplitOptionsUI,
  TTenantSplitOptionsDB
> = {
  fromDb: data => ({
    logins: data.logins,
    unsplittableResidencyIds: data.unsplittableResidencyIds || [],
    splits: data.splits.map(split => ({
      ...split,
      createdAt: split.createdAt
        ? formattedDateTimezone(split.createdAt, dateTimeFormat)
        : '',
      // A reversal is itself a split, but undoing it would re-fuse the profile, so it is
      // offered for the record only. The backend refuses anything else it cannot undo.
      canUndo: !split.undone && !split.reversal,
    })),
  }),
};
